import { useCallback, useRef } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVerticalIcon, Trash2Icon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MarkdownEditor } from "@/components/ui/markdown-editor";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import type { BaseTemplateAttribute } from "./types";

interface InstructionBlockProps {
  attribute: BaseTemplateAttribute;
  isLocked: boolean;
  hasBlockGap: boolean;
  onUpdateContent: (id: number, content: string) => void;
  onRemove: (id: number) => void;
}

export function InstructionBlock({
  attribute,
  isLocked,
  hasBlockGap,
  onUpdateContent,
  onRemove,
}: InstructionBlockProps) {
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: attribute.id.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  } as React.CSSProperties;

  const handleChange = useCallback(
    (html: string) => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onUpdateContent(attribute.id, html);
      }, 500);
    },
    [attribute.id, onUpdateContent],
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-attribute-id={attribute.id}
      className={
        "group/instruction border border-border bg-background" +
        (hasBlockGap ? " mt-3" : "")
      }
    >
      {/* Toolbar row */}
      <div className="flex items-center gap-1 px-2 py-1 border-b border-border opacity-0 group-hover/instruction:opacity-100 group-focus-within/instruction:opacity-100 transition-opacity">
        {!isLocked && (
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab p-0.5 text-muted-foreground hover:text-foreground"
            aria-label="Drag to reorder"
          >
            <GripVerticalIcon className="h-3 w-3" />
          </button>
        )}
        <span className="text-xs text-muted-foreground flex-1">Instruction</span>
        {!isLocked && (
          <DeleteConfirmDialog
            title="Delete Instruction"
            description="Are you sure you want to delete this instruction block? This cannot be undone."
            onConfirm={() => onRemove(attribute.id)}
            trigger={
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 text-muted-foreground hover:text-destructive"
              >
                <Trash2Icon className="h-3 w-3" />
              </Button>
            }
          />
        )}
      </div>

      {/* Editor */}
      <MarkdownEditor
        value={attribute.content_html || ""}
        onChange={handleChange}
        readOnly={isLocked}
        placeholder="Write instructions..."
        buttons={[
          "bold",
          "italic",
          "heading",
          "bulletList",
          "orderedList",
          "blockquote",
          "code",
        ]}
      />
    </div>
  );
}
